/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import ActionsMenu from '../../../components/shared/ActionsMenu'
import { useServiceContext } from '../../../root'
import type { Metadata } from '../../../services/api/metadata'
import ModalRemove from './Actions/ModalRemove'
import { useNavigate } from 'react-router'

interface TableMenuProps {
  property: Metadata
  onEditProperty: (property: Metadata) => void
  onDeleteSuccess?: () => void
  onExportStart?: () => void
  onExportEnd?: () => void
}

interface MenuItem {
  value: string
  action: () => void
  disabled?: boolean
}

const TableMenu: React.FC<TableMenuProps> = ({
  property,
  onEditProperty,
  onDeleteSuccess,
  onExportStart,
  onExportEnd
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const { toast } = context.service
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [items, setItems] = useState<MenuItem[]>([
    {
      value: 'LOADING...',
      action: () => {},
      disabled: true
    }
  ])

  const handleExport = async () => {
    try {
      // Start loading
      if (onExportStart) {
        onExportStart()
      }

      // Export single property with entityId parameter
      await context.api.settings.exportSettingsData('PROPERTIES', undefined, property.id, translate)
    } catch (error) {
      toast.error(translate('SETTINGS_IMPORT_FAIL'))
    } finally {
      // End loading
      if (onExportEnd) {
        onExportEnd()
      }
    }
  }

  const handleActivity = () => {
    // Navigate to settings activity page with proprties parameters
    const params = new URLSearchParams({
      id: property.id.toString(),
      type: 'METADATA_TPL',
      itemName: property.name
    })
    navigate(`/settings/activity?${params.toString()}`)
  }

  const setOptions = (prop: Metadata) => {
    const menu: MenuItem[] = []

    menu.push({
      value: 'ACTION_EDIT',
      action: () => {
        onEditProperty(prop)
      }
    })

    menu.push({
      value: 'ACTION_REMOVE',
      action: () => {
        setShowRemoveModal(true)
      }
    })

    menu.push({
      value: 'ACTION_EXPORT',
      action: () => {
        handleExport()
      }
    })

    menu.push({
      value: 'BROWSER_ACTION_ACTIVITY',
      action: () => {
        handleActivity()
      }
    })

    setItems(menu)
  }

  useEffect(() => {
    setOptions(property)
  }, [property, isOpen])

  return (
    <>
      <ActionsMenu
        menuId={`action-menu-property-${property.id}`}
        onToggle={(open) => {
          setIsOpen(open)
        }}
        items={items}
      />
      {showRemoveModal && (
        <ModalRemove
          property={property}
          onClose={() => setShowRemoveModal(false)}
          onSuccess={() => {
            setShowRemoveModal(false)
            if (onDeleteSuccess) {
              onDeleteSuccess()
            }
          }}
        />
      )}
    </>
  )
}

export default TableMenu
